import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// attach bearer as backup
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function fmt(n) {
  const v = Number(n) || 0;
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

// Convert a base64 data URL into a Blob URL and open it (browsers block navigating to
// data:… URLs at the top level as anti-phishing; blob: URLs are allowed).
export function openDataUrlInTab(dataUrl) {
  if (!dataUrl) return;
  try {
    if (!dataUrl.startsWith("data:")) {
      // Regular http(s) URL — open directly
      const w = window.open(dataUrl, "_blank", "noopener,noreferrer");
      if (!w) throw new Error("popup blocked");
      return;
    }
    const [head, b64] = dataUrl.split(",");
    const mime = /data:(.*?);base64/.exec(head)?.[1] || "application/octet-stream";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) throw new Error("popup blocked");
    // Revoke after the tab has had time to load
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    // Fallback: navigate current tab to the blob so it still opens on mobile
    try {
      const [head, b64] = dataUrl.split(",");
      const mime = /data:(.*?);base64/.exec(head)?.[1] || "application/octet-stream";
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      const url = URL.createObjectURL(blob);
      window.location.href = url;
    } catch {
      alert("Could not open the file. Please try Download instead.");
    }
  }
}

// Trigger a real file download from a base64 data URL (bypasses browser blocks).
export function downloadDataUrl(dataUrl, filename) {
  if (!dataUrl) return;
  try {
    const [head, b64] = dataUrl.split(",");
    const mime = /data:(.*?);base64/.exec(head)?.[1] || "application/octet-stream";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "document";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch {
    // Last resort — direct link
    const a = document.createElement("a");
    a.href = dataUrl; a.download = filename || "document";
    document.body.appendChild(a); a.click(); a.remove();
  }
}

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

export function validityStatus(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return { label: "—", tone: "muted" };
  if (d < 0) return { label: "Expired", tone: "danger" };
  if (d <= 7) return { label: `${d}d left`, tone: "warn" };
  if (d <= 30) return { label: `${d}d left`, tone: "info" };
  return { label: `${d}d left`, tone: "ok" };
}

export function toneClass(tone) {
  return {
    danger: "bg-red-500/15 text-red-400 border-red-500/30",
    warn: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    info: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    muted: "bg-slate-500/10 text-slate-400 border-slate-500/20",
  }[tone] || "bg-slate-500/10 text-slate-400 border-slate-500/20";
}

export function whatsappShare(text) {
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank");
}

export function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function downloadPDF(title, rows) {
  // simple print-to-pdf via new window
  const w = window.open("", "_blank");
  const keys = rows.length ? Object.keys(rows[0]) : [];
  const style = `<style>body{font-family:sans-serif;padding:24px;color:#0b1220}h1{margin:0 0 12px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#f1f5f9}</style>`;
  const table = `<table><thead><tr>${keys.map((k) => `<th>${k}</th>`).join("")}</tr></thead><tbody>${rows
    .map((r) => `<tr>${keys.map((k) => `<td>${(r[k] ?? "").toString().replace(/</g, "&lt;")}</td>`).join("")}</tr>`)
    .join("")}</tbody></table>`;
  w.document.write(`<html><head><title>${title}</title>${style}</head><body><h1>${title}</h1>${table}<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}
